import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import teamService from '../services/teamService';
import { useNavigate } from 'react-router-dom';
import useNotification from '../hooks/useNotification';

/**
 * ManageTeams component
 * Displays a list of teams with sorting and filtering options
 */
const ManageTeams = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTeams, setFilteredTeams] = useState([]);

  // Fetch teams on component mount
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const response = await teamService.getUserTeams();
        setTeams(response.data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching teams:', error);
        showNotification({
          message: error.message || 'Failed to fetch teams',
          type: 'error'
        });
        setLoading(false);
      }
    };

    fetchTeams();
  }, [showNotification]);

  // Filter teams based on tab and search query
  useEffect(() => {
    if (!teams.length) {
      setFilteredTeams([]);
      return;
    }

    let filtered = [...teams];

    // Apply tab filter
    if (tabValue === 0) { // Active
      filtered = filtered.filter(team => !team.isArchived);
    } else if (tabValue === 1) { // Drafts - example condition, adjust as needed
      filtered = filtered.filter(team => !team.isArchived && team.members.length <= 1);
    } else if (tabValue === 2) { // Archive
      filtered = filtered.filter(team => team.isArchived);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(team => 
        team.name.toLowerCase().includes(query) || 
        (team.description && team.description.toLowerCase().includes(query))
      );
    }

    setFilteredTeams(filtered);
  }, [teams, tabValue, searchQuery]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle edit team
  const handleEditTeam = (teamId) => {
    navigate(`/teams/create?teamId=${teamId}`);
  };

  // Handle view team
  const handleViewTeam = (teamId) => {
    navigate(`/teams/${teamId}`);
  };

  // Handle delete team
  const handleDeleteTeam = (teamId) => {
    // Implement deletion logic or confirmation dialog
    console.log(`Delete team: ${teamId}`);
  };

  // Render the loading state
  if (loading) {
    return (
      <Box sx={{ padding: 3, textAlign: 'center' }}>
        <Typography variant="h6">Loading teams...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '20px'
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 500,
            color: '#475569'
          }}
        >
          Manage teams
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/teams/create')}
          sx={{
            bgcolor: '#cf649a',
            color: 'white',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: '4px',
            '&:hover': {
              bgcolor: '#b85586'
            }
          }}
        >
          Create Team
        </Button>
      </Box>

      {/* Tabs for filtering */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="team status tabs"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500
            }
          }}
        >
          <Tab label="Active" />
          <Tab label="Drafts" />
          <Tab label="Archive" />
        </Tabs>
      </Box>

      {/* Search box */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              '& fieldset': {
                borderColor: '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: '#cbd5e1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#94a3b8',
              },
            },
          }}
        />
      </Box>

      {/* Teams table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none',
          border: 'none',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell sx={{ 
                fontWeight: 600, 
                color: '#475569',
                fontSize: '14px',
                pl: 3
              }}>
                Team name
              </TableCell>
              <TableCell align="right" sx={{ 
                fontWeight: 600, 
                color: '#475569',
                fontSize: '14px',
                pr: 3
              }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTeams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    No teams found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTeams.map((team) => (
                <TableRow 
                  key={team._id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { bgcolor: '#f8fafc' }
                  }}
                >
                  <TableCell 
                    component="th" 
                    scope="row"
                    sx={{ 
                      pl: 3,
                      fontSize: '14px',
                      color: '#334155'
                    }}
                  >
                    {team.name}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 3 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditTeam(team._id)}
                      sx={{ color: '#64748b' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewTeam(team._id)}
                      sx={{ color: '#64748b', ml: 1 }}
                    >
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteTeam(team._id)}
                      sx={{ color: '#64748b', ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ManageTeams;